import React, { PropsWithChildren, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import InfoIcon from "@mui/icons-material/Info";
import InsertChartIcon from "@mui/icons-material/InsertChart";
import { Box, Button, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { GetDataPath, GetRowIdParams, GridOptions } from "ag-grid-community";
import get from "lodash/get";

import analytics from "fond/analytics";
import { selectProjectAccount, useGetVersionBomQuery, useGetVersionStatusQuery } from "fond/api";
import { makeRule } from "fond/architecture/bomRules";
import useProjectHasScenarioConfiguration from "fond/hooks/useProjectHasScenarioConfiguration";
import DesignNeedsUpdating from "fond/project/DesignNeedsUpdating";
import { showUpdateDesignBanner } from "fond/project/helpers";
import { BomOutput, Maybe, Project, Store } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { formatNumber } from "fond/utils/number";
import { isCollaborationProject } from "fond/utils/project";
import { AgGrid, BlockSpinner, Message, Modal, NonIdealState } from "fond/widgets";

import { BomModalProps, BomTreeRowData, columnDefs, flattenBomData } from "./index";
import { load } from "./redux";
import { RegenerateBomBanner } from "./RegenerateBomBanner";

import { GridContainer, NoBomMessage, Paragraph, SumBar, SumBarTitle, SumBarTotal } from "./GridBomModal.styles";

const gridOptions: GridOptions<BomTreeRowData> = {
  suppressAggFuncInHeader: true,
  sideBar: false,
  pagination: false,
  getRowId: (params: GetRowIdParams<BomTreeRowData>) => params.data.path.join("-"),
  defaultColDef: {
    sortable: true,
    resizable: true,
    filter: false,
  },
  rowGroupPanelShow: "never",
  autoGroupColumnDef: {
    headerName: "Item",
    flex: 1.5,
    cellRendererParams: {
      suppressCount: true,
      innerRenderer: (params: { value: string; data: BomTreeRowData; node: { footer: boolean } }) => {
        if (params.node?.footer) return "Total";
        return params.data?.name ?? params.value ?? "";
      },
    },
  },
  groupDefaultExpanded: 1,
  groupTotalRow: (params) => (params.node.level === 0 ? "bottom" : undefined),
  suppressStickyTotalRow: "group",
};

interface BomGridContentProps {
  project: Project;
  bom: BomOutput;
}

const getDataPath: GetDataPath<BomTreeRowData> = (data) => data.path;

const BomGridContent: React.FC<PropsWithChildren<BomGridContentProps>> = ({ project, bom, children }) => {
  const rowData = useMemo(() => flattenBomData(bom, project.SystemOfMeasurement), [bom, project.SystemOfMeasurement]);
  return (
    <>
      <SumBar>
        <Box>
          <SumBarTitle variant="h6" color="inherit">
            {project.ProjectName}
          </SumBarTitle>
        </Box>
        <Box display="flex" flexDirection="row">
          <SumBarTotal variant="h6" color="inherit">
            Total cost:
          </SumBarTotal>
          <Typography variant="h6" color="inherit" data-testid="grand-total">
            {formatNumber(bom.total)}
          </Typography>
        </Box>
      </SumBar>
      {children}
      <GridContainer>
        <AgGrid
          columnDefs={columnDefs}
          rowData={rowData}
          treeData
          getDataPath={getDataPath}
          gridOptions={gridOptions}
          size="compact"
          variant="outlined"
        />
      </GridContainer>
    </>
  );
};

const PlannerBomModal: React.FC<BomModalProps> = ({ onClose, title, project, architecture }: BomModalProps) => {
  // A future improvement here could be to move everything out of redux and use RTK query instead; that would
  // more closely align the 'planner' and 'collaboration' code paths.
  const dispatch = useAppDispatch();
  const bom = useSelector((state: Store) => state.bom.bom);
  const loadStatus = useSelector((state: Store) => state.bom.loadStatus);
  const generateStatus = useSelector((state: Store) => state.bom.generateStatus);
  const bomArchRestrictions = useSelector((state: Store) => selectProjectAccount(state, project)?.BomArchRestrictions);
  const versionId = useSelector((state: Store) => state.project.versionId);
  const { data: versionStatus } = useGetVersionStatusQuery(versionId, { skip: !versionId });
  const showDesignBanner = versionStatus ? showUpdateDesignBanner(project, versionStatus) : false;

  const hasBom = bom != null && (loadStatus === "success" || loadStatus === "dismissed");

  // We currently only do arch-BOM validation for projects without custom
  // BOMs (ie. using Biarri-created rulesets).
  let errors = [];
  if (hasBom && architecture != null && architecture.BOM == null && Array.isArray(bomArchRestrictions)) {
    for (const restriction of bomArchRestrictions) {
      const rule = makeRule(restriction);
      const val = get(architecture, restriction[0]);
      if (val !== undefined && rule.condition(val, architecture, null, "metric")) {
        errors.push({
          key: restriction.flat().join("-"),
          message: rule.bomMessage(val, project.SystemOfMeasurement),
        });
      }
    }
  }

  useEffect(() => {
    analytics.track("load project bom.");
    dispatch(load(versionId));
  }, [versionId, dispatch]);

  return (
    <BaseBomModal onClose={onClose} title={title}>
      {project == null || versionStatus?.Status === "Running" || loadStatus === "executing" || generateStatus === "executing" ? (
        <NonIdealState
          icon={<BlockSpinner />}
          title="Project is currently running"
          description="The bill of materials will load once the project has finished executing. Please return to the previous page."
        />
      ) : (
        <>
          {showDesignBanner && <DesignNeedsUpdating />}
          {!showDesignBanner && versionStatus?.BomHasChanged && <RegenerateBomBanner />}
          {!hasBom ? (
            <NoBomMessage data-testid="no-bom" type="info" typography="h6" icon={<InfoIcon />}>
              To see the bill of materials for this project, first generate a design.
            </NoBomMessage>
          ) : (
            <BomGridContent project={project} bom={bom}>
              <Paragraph>
                The materials and costs can be configured in the Architecture Panel. If there is no BOM configured for this architecture, the default
                BOM for your FOND account is displayed here.
              </Paragraph>
              <Paragraph>
                {"Read our article on how to "}
                <a href="https://fondhelp.biarrinetworks.com/configuring-your-bom" target="_blank" rel="noopener noreferrer">
                  configure a BOM
                </a>
                {" for more information."}
              </Paragraph>
              {errors.length > 0 && (
                <Message type="warning" data-testid="bom-arch-validation-warning" mb={3}>
                  <Box display="flex" flexDirection="column">
                    <div>
                      This Bill of Materials has not been configured with your architecture in mind. Your architecture contains parameters that result
                      in equipment being placed that will not be counted. The equipment that won't be counted are:
                    </div>
                    <ul>
                      {errors.map((e) => (
                        <li key={e.key}>{e.message}</li>
                      ))}
                    </ul>
                  </Box>
                </Message>
              )}
            </BomGridContent>
          )}
        </>
      )}
    </BaseBomModal>
  );
};

type CollaborationBomModalProps = Omit<BomModalProps, "architecture">;
type CollaborationBomState = "unavailable" | "loading" | "error" | "ready";

const getBomState = (hasConfig: boolean, isLoadingBom: boolean, isBomError: boolean, bom: Maybe<BomOutput>): CollaborationBomState => {
  if (!hasConfig) return "unavailable";
  if (isLoadingBom) return "loading";
  if (isBomError || !bom) return "error";

  return "ready";
};

const CollaborationBomModal: React.FC<CollaborationBomModalProps> = ({ onClose, title, project }: CollaborationBomModalProps) => {
  const { hasConfig } = useProjectHasScenarioConfiguration({ projectId: project.ID });
  const versionId = useSelector((state: Store) => state.project.versionId);
  const {
    data: bom,
    isError: isBomError,
    isLoading: isLoadingBom,
  } = useGetVersionBomQuery(versionId || skipToken, { skip: !versionId || !hasConfig });

  if (!versionId) return null;
  const state = getBomState(hasConfig, isLoadingBom, isBomError, bom);

  return (
    <BaseBomModal onClose={onClose} title={title}>
      {state === "unavailable" && (
        <Message type="info" data-testid="collaboration-bom-message">
          The bill of materials is not available for collaboration projects.
        </Message>
      )}
      {state === "loading" && <BlockSpinner />}
      {state === "error" && (
        <Message type="error" data-testid="collaboration-bom-error-message">
          The bill of materials could not be loaded.
        </Message>
      )}
      {state === "ready" && bom && <BomGridContent project={project} bom={bom} />}
    </BaseBomModal>
  );
};

interface BaseBomModalProps {
  onClose: () => void;
  title?: string;
}

const BaseBomModal: React.FC<PropsWithChildren<BaseBomModalProps>> = ({ onClose, title, children }) => {
  return (
    <Modal
      open
      onClose={onClose}
      variant="primary"
      header={title}
      headerIcon={<InsertChartIcon />}
      headerSubtitle="COLLABORATE"
      data-testid="bom-modal"
      PaperProps={{ style: { width: "max(50vw, 1100px)", maxWidth: "max(50vw, 1100px)" } }}
      content={children}
      actions={
        <Button data-testid="bom-modal-close-button" onClick={onClose}>
          Close
        </Button>
      }
    />
  );
};

const GridBomModal = ({ title, project, architecture, onClose }: BomModalProps) => {
  if (isCollaborationProject(project)) {
    return <CollaborationBomModal project={project} onClose={onClose} title={title} />;
  }

  return <PlannerBomModal project={project} architecture={architecture} onClose={onClose} title={title} />;
};

export default GridBomModal;
